import React, { useState, useEffect, useCallback } from 'react';
import connectionStatsService from '../services/connectionStatsService';
import './OhipTransactionsTab.css';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PERIOD_OPTIONS = [
    { label: 'Last 3 months',  months: 3 },
    { label: 'Last 6 months',  months: 6 },
    { label: 'Last 12 months', months: 12 },
];

function monthLabel(year, month) {
    return `${MONTH_NAMES[month - 1]} ${year}`;
}

function dayLabel(year, month, day) {
    return `${day} ${MONTH_NAMES[month - 1]} ${year}`;
}

// entityType: 'partner' | 'chain' | 'hotel'
// entityId: numeric ID
const OhipTransactionsTab = ({ entityType, entityId }) => {
    const [months, setMonths]       = useState(3);
    const [groupBy, setGroupBy]     = useState('month'); // 'month' | 'day'
    const [search, setSearch]       = useState('');
    const [rows, setRows]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);

    const load = useCallback(async () => {
        if (!entityId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await connectionStatsService.getStats(entityType, entityId, months);
            setRows(data.rows || []);
        } catch (err) {
            setError('Failed to load transaction data.');
        } finally {
            setLoading(false);
        }
    }, [entityType, entityId, months]);

    useEffect(() => { load(); }, [load]);

    const showChain = entityType === 'partner';
    const showHotel = entityType === 'partner' || entityType === 'chain';

    const searchLower = search.toLowerCase();
    const filteredRows = searchLower
        ? rows.filter(r =>
            (r.marketplace_name || '').toLowerCase().includes(searchLower) ||
            (r.chain_name       || '').toLowerCase().includes(searchLower) ||
            (r.hotel_name       || '').toLowerCase().includes(searchLower))
        : rows;

    // ── Month grouping ──
    // Pivot: rows become entity keys; columns become unique months newest-first.
    const monthPeriods = [...new Set(
        filteredRows.map(r => `${r.year}-${String(r.month).padStart(2, '0')}`)
    )].sort().reverse().map(p => {
        const [y, m] = p.split('-');
        return { year: parseInt(y, 10), month: parseInt(m, 10) };
    });

    const monthEntityMap = new Map();
    filteredRows.forEach(r => {
        const key = `${r.marketplace_id}||${r.chain_id || ''}||${r.hotel_id || ''}`;
        if (!monthEntityMap.has(key)) {
            monthEntityMap.set(key, {
                marketplace_id: r.marketplace_id,
                marketplace_name: r.marketplace_name,
                chain_id: r.chain_id,
                chain_name: r.chain_name,
                hotel_id: r.hotel_id,
                hotel_name: r.hotel_name,
                byMonth: {},
                total: 0,
            });
        }
        const e = monthEntityMap.get(key);
        const mk = `${r.year}-${r.month}`;
        e.byMonth[mk] = (e.byMonth[mk] || 0) + Number(r.call_count);
        e.total += Number(r.call_count);
    });

    const monthEntities = [...monthEntityMap.values()].sort((a, b) => {
        const pc = (a.marketplace_name || '').localeCompare(b.marketplace_name || '');
        if (pc !== 0) return pc;
        const cc = (a.chain_name || '').localeCompare(b.chain_name || '');
        if (cc !== 0) return cc;
        return (a.hotel_name || '').localeCompare(b.hotel_name || '');
    });

    const grandMonthTotals = {};
    let grandMonthTotal = 0;
    monthEntities.forEach(e => {
        grandMonthTotal += e.total;
        Object.entries(e.byMonth).forEach(([k, v]) => {
            grandMonthTotals[k] = (grandMonthTotals[k] || 0) + v;
        });
    });

    // ── Day grouping ──
    // Flat list sorted newest date first; entity info on each row.
    const dayRows = [...filteredRows]
        .map(r => ({ ...r, call_count: Number(r.call_count) }))
        .sort((a, b) => {
            const da = a.year * 10000 + a.month * 100 + a.day;
            const db2 = b.year * 10000 + b.month * 100 + b.day;
            if (db2 !== da) return db2 - da;
            const pc = (a.marketplace_name || '').localeCompare(b.marketplace_name || '');
            if (pc !== 0) return pc;
            const cc = (a.chain_name || '').localeCompare(b.chain_name || '');
            if (cc !== 0) return cc;
            return (a.hotel_name || '').localeCompare(b.hotel_name || '');
        });

    const grandDayTotal = dayRows.reduce((s, r) => s + r.call_count, 0);

    const isEmpty = filteredRows.length === 0;
    const fixedColCount = 1 + (showChain ? 1 : 0) + (showHotel ? 1 : 0);

    return (
        <div className="ohip-transactions">
            <div style={{ marginBottom: '0.75rem' }}>
                <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                    OHIP Transactions
                </h3>
                <p style={{ margin: '0 0 0.875rem', fontSize: '0.875rem', color: '#64748b' }}>
                    API calls made to Oracle OHIP, broken down by product and period.
                </p>
            </div>

            <div className="ohip-transactions-controls">
                {PERIOD_OPTIONS.map(opt => (
                    <button
                        key={opt.months}
                        type="button"
                        className={`ohip-transactions-period-btn${months === opt.months ? ' active' : ''}`}
                        onClick={() => setMonths(opt.months)}
                    >
                        {opt.label}
                    </button>
                ))}

                <input
                    type="text"
                    placeholder="Search…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="ohip-transactions-search"
                />

                <button
                    type="button"
                    className="ohip-transactions-period-btn"
                    onClick={load}
                    disabled={loading}
                    title="Refresh"
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, transform: loading ? 'rotate(360deg)' : 'none', transition: loading ? 'transform 0.6s linear' : 'none' }}>
                        <path d="M13.65 2.35A8 8 0 1 0 15 8h-2a6 6 0 1 1-1.76-4.24L9 6h6V0l-1.35 2.35z" fill="currentColor"/>
                    </svg>
                    Refresh
                </button>

                <div className="ohip-transactions-group-toggle">
                    <button
                        type="button"
                        className={`ohip-transactions-period-btn${groupBy === 'month' ? ' active' : ''}`}
                        onClick={() => setGroupBy('month')}
                    >
                        By Month
                    </button>
                    <button
                        type="button"
                        className={`ohip-transactions-period-btn${groupBy === 'day' ? ' active' : ''}`}
                        onClick={() => setGroupBy('day')}
                    >
                        By Day
                    </button>
                </div>
            </div>

            {loading && <div className="ohip-transactions-loading">Loading…</div>}
            {error   && <div className="ohip-transactions-error">{error}</div>}

            {!loading && !error && isEmpty && (
                <div className="ohip-transactions-empty">No OHIP transactions recorded for this period.</div>
            )}

            {/* ── Month pivot table ── */}
            {!loading && !error && !isEmpty && groupBy === 'month' && (
                <div style={{ overflowX: 'auto' }}>
                    <table className="ohip-transactions-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                {showChain && <th>Chain</th>}
                                {showHotel && <th>Hotel</th>}
                                {monthPeriods.map(p => (
                                    <th key={`${p.year}-${p.month}`} style={{ textAlign: 'right' }}>
                                        {monthLabel(p.year, p.month)}
                                    </th>
                                ))}
                                <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monthEntities.map((e, i) => (
                                <tr key={i}>
                                    <td>{e.marketplace_name || `Marketplace ${e.marketplace_id}`}</td>
                                    {showChain && <td style={{ color: '#374151' }}>{e.chain_name || '—'}</td>}
                                    {showHotel && <td style={{ color: '#374151' }}>{e.hotel_name || '—'}</td>}
                                    {monthPeriods.map(p => {
                                        const val = e.byMonth[`${p.year}-${p.month}`];
                                        return (
                                            <td key={`${p.year}-${p.month}`} className="ohip-transactions-count">
                                                {val ? val.toLocaleString() : '—'}
                                            </td>
                                        );
                                    })}
                                    <td className="ohip-transactions-count">{e.total.toLocaleString()}</td>
                                </tr>
                            ))}
                            <tr className="ohip-transactions-total-row">
                                <td colSpan={fixedColCount}>Total</td>
                                {monthPeriods.map(p => {
                                    const val = grandMonthTotals[`${p.year}-${p.month}`];
                                    return (
                                        <td key={`${p.year}-${p.month}`} className="ohip-transactions-count">
                                            {val ? val.toLocaleString() : '—'}
                                        </td>
                                    );
                                })}
                                <td className="ohip-transactions-count">{grandMonthTotal.toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Day list table ── */}
            {!loading && !error && !isEmpty && groupBy === 'day' && (
                <div style={{ overflowX: 'auto' }}>
                    <table className="ohip-transactions-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Product</th>
                                {showChain && <th>Chain</th>}
                                {showHotel && <th>Hotel</th>}
                                <th style={{ textAlign: 'right' }}>Calls</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dayRows.map((r, i) => (
                                <tr key={i}>
                                    <td style={{ whiteSpace: 'nowrap', color: '#374151' }}>
                                        {dayLabel(r.year, r.month, r.day)}
                                    </td>
                                    <td>{r.marketplace_name || `Marketplace ${r.marketplace_id}`}</td>
                                    {showChain && <td style={{ color: '#374151' }}>{r.chain_name || '—'}</td>}
                                    {showHotel && <td style={{ color: '#374151' }}>{r.hotel_name || '—'}</td>}
                                    <td className="ohip-transactions-count">{r.call_count.toLocaleString()}</td>
                                </tr>
                            ))}
                            <tr className="ohip-transactions-total-row">
                                <td colSpan={fixedColCount + 1}>Total</td>
                                <td className="ohip-transactions-count">{grandDayTotal.toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default OhipTransactionsTab;
